import React, { useRef, useState } from "react";
import { Paperclip, X } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const POSITIONS = ["Kitchen & Culinary", "Bar & Beverage", "Humidor & Cigar Service", "Front of House", "Concierge & Membership", "Management", "Other"];

export default function Careers() {
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: "", message: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Résumé must be under 5 MB."); return; }
    const ok = /\.(pdf|doc|docx)$/i.test(f.name);
    if (!ok) { toast.error("Please attach a PDF or Word document."); return; }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("resume", file);
      await api.post("/careers/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <section className="min-h-screen pt-40 pb-24 px-6 sm:px-10 lg:px-16" data-testid="careers-success">
        <div className="max-w-3xl mx-auto">
          <p className="eyebrow">Join Us</p>
          <h1 className="font-serif text-5xl mt-4">Received.</h1>
          <div className="hairline my-10 max-w-[80px]" />
          <p className="text-[var(--twr-warm-white)]/75 leading-relaxed text-lg">
            Thank you — your application is in front of the right people. If your standards match ours, you will hear from us.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen pt-40 pb-24 px-6 sm:px-10 lg:px-16" data-testid="careers-page">
      <div className="max-w-3xl mx-auto">
        <p className="eyebrow">Join Us</p>
        <h1 className="font-serif text-5xl mt-4">Careers</h1>
        <div className="hairline my-10 max-w-[80px]" />
        <p className="text-[var(--twr-warm-white)]/75 leading-relaxed text-lg">
          We are always quietly looking for people who share our standards — in the kitchen, behind the bar, in the humidor, and at the door.
        </p>

        <form onSubmit={submit} className="mt-14 space-y-6" data-testid="careers-form">
          <div className="grid sm:grid-cols-2 gap-6">
            <label className="block">
              <span className="eyebrow">Full Name *</span>
              <input data-testid="careers-name" required value={form.name} onChange={(e) => set("name", e.target.value)}
                className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-sm" />
            </label>
            <label className="block">
              <span className="eyebrow">Email *</span>
              <input data-testid="careers-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
                className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-sm" />
            </label>
            <label className="block">
              <span className="eyebrow">Phone</span>
              <input data-testid="careers-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-sm" />
            </label>
            <label className="block">
              <span className="eyebrow">Position of Interest</span>
              <select data-testid="careers-position" value={form.position} onChange={(e) => set("position", e.target.value)}
                className="mt-3 w-full bg-[#0A0A0A] border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-sm">
                <option value="">Select…</option>
                {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="eyebrow">Tell us your story</span>
            <textarea data-testid="careers-message" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)}
              placeholder="Experience, standards, and why The Win Room."
              className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-sm" />
          </label>

          <div>
            <span className="eyebrow">Résumé</span>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={pickFile} className="hidden" data-testid="careers-resume-input" />
            {!file ? (
              <button type="button" data-testid="careers-attach-btn" onClick={() => fileRef.current?.click()}
                className="mt-3 flex items-center gap-3 w-full border border-dashed border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)] transition-colors px-4 py-4 text-sm text-[var(--twr-warm-white)]/65">
                <Paperclip size={15} strokeWidth={1.5} />
                Attach your résumé — PDF or Word, up to 5 MB
              </button>
            ) : (
              <div data-testid="careers-resume-attached" className="mt-3 flex items-center justify-between gap-3 border border-[var(--twr-bronze)]/50 px-4 py-4 text-sm">
                <span className="flex items-center gap-3 min-w-0">
                  <Paperclip size={15} strokeWidth={1.5} className="text-[var(--twr-bronze)] shrink-0" />
                  <span className="truncate">{file.name}</span>
                  <span className="text-[var(--twr-warm-white)]/45 text-[12px] shrink-0">{(file.size / 1024).toFixed(0)} KB</span>
                </span>
                <button type="button" data-testid="careers-resume-remove" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                  className="text-[var(--twr-warm-white)]/55 hover:text-red-400 transition-colors shrink-0"><X size={15} /></button>
              </div>
            )}
          </div>

          <button disabled={busy} data-testid="careers-submit" className="btn-bronze">
            {busy ? "Sending…" : "Submit Application"}
          </button>
        </form>
      </div>
    </section>
  );
}
